export type NavigationTimingType = "navigate" | "reload" | "back_forward" | "prerender";

const RELOAD_BACK_BLOCK_SESSION_KEY = "admin-center:reload-back-block";

/**
 * Returns true when the current page load was triggered by a full refresh (F5 / reload).
 */
export function wasPageReloaded(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    if (typeof window.performance?.getEntriesByType === "function") {
      const entries = window.performance.getEntriesByType("navigation");
      if (entries.length > 0) {
        return (entries[0] as PerformanceNavigationTiming).type === "reload";
      }
      return false;
    }

    const legacyNavigation = (
      window.performance as Performance & {
        navigation?: { type?: number; TYPE_RELOAD?: number };
      }
    ).navigation;

    if (legacyNavigation?.TYPE_RELOAD !== undefined) {
      return legacyNavigation.type === legacyNavigation.TYPE_RELOAD;
    }
  } catch {
    return false;
  }

  return false;
}

export function isReloadBackBlockSession(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    return sessionStorage.getItem(RELOAD_BACK_BLOCK_SESSION_KEY) === "1";
  } catch {
    return false;
  }
}

export function markReloadBackBlockSession(): void {
  if (typeof window === "undefined") {
    return;
  }

  try {
    sessionStorage.setItem(RELOAD_BACK_BLOCK_SESSION_KEY, "1");
  } catch {
    // Ignore storage failures so navigation is not blocked.
  }
}

/**
 * Returns true when browser back should be blocked for this tab session.
 */
export function shouldBlockBrowserBackAfterReload(): boolean {
  if (isReloadBackBlockSession()) {
    return true;
  }

  if (wasPageReloaded()) {
    markReloadBackBlockSession();
    return true;
  }

  return false;
}

/**
 * Traps the browser back button by keeping the current URL on the history stack.
 * Returns a cleanup function that removes the listener.
 */
export function installBrowserBackBlock(): () => void {
  const cleanups: Array<() => void> = [];

  try {
    const trappedUrl = window.location.href;

    // Keep the current entry's state (react-router keeps location.state / idx there) so a
    // reload does not wipe the state that pages like UAM View read on mount.
    const pushTrapState = (): void => {
      window.history.pushState(window.history.state, "", trappedUrl);
    };

    pushTrapState();

    const handlePopState = (): void => {
      setTimeout(() => {
        try {
          pushTrapState();

          if (window.location.href !== trappedUrl) {
            window.history.go(1);
          }
        } catch {
          // Ignore history API failures so navigation is not blocked.
        }
      }, 0);
    };

    window.addEventListener("popstate", handlePopState, true);
    cleanups.push(() => {
      window.removeEventListener("popstate", handlePopState, true);
    });

    const refreshTrapOnActivation = (): void => {
      try {
        window.history.pushState(window.history.state, "", window.location.href);
      } catch {
        // Ignore history API failures so navigation is not blocked.
      }

      activationEvents.forEach((eventName) => {
        document.removeEventListener(eventName, refreshTrapOnActivation, true);
      });
    };

    const activationEvents = ["click", "keydown", "touchstart"] as const;
    activationEvents.forEach((eventName) => {
      document.addEventListener(eventName, refreshTrapOnActivation, true);
      cleanups.push(() => {
        document.removeEventListener(eventName, refreshTrapOnActivation, true);
      });
    });
  } catch {
    return () => undefined;
  }

  return () => {
    cleanups.forEach((cleanup) => {
      cleanup();
    });
  };
}

let bootstrapInstalled = false;

/**
 * Installs the history trap as early as possible after a reload.
 * Chromium/Edge skip pushState entries without user activation, so this must
 * run synchronously during startup while reload activation may still apply.
 */
export function bootstrapBrowserBackBlockAfterReload(): void {
  if (bootstrapInstalled) {
    return;
  }

  try {
    if (!shouldBlockBrowserBackAfterReload()) {
      return;
    }

    bootstrapInstalled = true;
    installBrowserBackBlock();
  } catch {
    // Ignore bootstrap failures so app startup is not blocked.
  }
}

/**
 * When the page was loaded via refresh, prevent browser back navigation.
 */
export function disableBrowserBackAfterReload(): (() => void) | undefined {
  try {
    if (!shouldBlockBrowserBackAfterReload()) {
      return undefined;
    }

    return installBrowserBackBlock();
  } catch {
    return undefined;
  }
}
